/**
 * BrandListPresenter
 * --
 */
import React, { useEffect, useState } from 'react';
import {
  Content,
  Title,
  Row,
  Col,
  ExpandableContainer,
  ModalLayout,
  Statistic,
  // Table,
} from 'components';
import {
  Button,
  DatePicker,
  Input,
  Select,
  Table,
  Tag,
  Radio,
  Spin,
} from 'antd';
import moment from 'moment';
import { EditOutlined, PaperClipOutlined } from '@ant-design/icons';
import {
  CORRECTION_STATUS,
  APPLICATION_STATUS,
} from 'constants/ShopInfomationConstants';
import BrandDetail from '../BrandDetail';
import MessageAlert from 'utils';
import { downloadXlsx } from 'utils/XlsxDownloader';

const initialSearchFilter = {
  keywordType: 'shop_information_name',
  keyword: '',
  dateType: 'all', // [all, week, month, 3month]
  startDate: null,
  endDate: null,
  applicationStatus: 'all',
};

/**----------------------------------------
 * [Component] 입점업체 목록
 * ----------------------------------------
 */
const BrandListPresenter = ({ shopList, onChangeShopInformation }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState(initialSearchFilter);
  const [currentShop, setCurrentShop] = useState(null);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  /* ===== HOOKS ===== */
  useEffect(() => {
    setData(shopList);
  }, [shopList]);

  /* ===== FUNCTIONS ===== */
  /**
   * 필터 값 바인딩
   * --
   */
  const handleChangeFilter = (key, value) => {
    setSearchFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * 필터 값 초기화
   * --
   */
  const handleReset = () => {
    setLoading(true);

    setTimeout(() => {
      setSearchFilter(initialSearchFilter);
      setData(shopList);
      setLoading(false);
    }, 800);
  };

  /**
   * 필터 값 검색
   * --
   */
  const handleSearch = () => {
    setLoading(true);
    try {
      const { applicationStatus, dateType, keywordType, keyword } =
        searchFilter;
      let endDate = moment().endOf().unix();
      let startDate;
      if (dateType === 'week') {
        startDate = moment().add(-7, 'days').startOf().unix();
      } else if (dateType === 'month') {
        startDate = moment().add(-30, 'days').startOf().unix();
      } else if (dateType === '3month') {
        startDate = moment().add(-90, 'days').startOf().unix();
      }

      const filtered = shopList
        .filter((item) =>
          applicationStatus === 'all'
            ? true
            : item.application_status === applicationStatus
            ? true
            : false
        )
        .filter((item) =>
          dateType === 'all'
            ? true
            : item.created_at >= startDate && item.created_at <= endDate
        )
        .filter((item) => item[keywordType].indexOf(keyword) >= 0);

      setTimeout(() => {
        setData(filtered);
        setLoading(false);
      }, 800);
    } catch (e) {
      setLoading(false);
      console.log('e:', e);
    }
  };

  /**
   * 페이지 네이션 함수
   * @param {number} page
   * --
   */
  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  /* ===== VARIABLE ===== */
  const formItem = [
    {
      label: '검색어',
      form: [
        {
          input: (
            <Select
              value={searchFilter.keywordType}
              style={{ width: 100, marginRight: 5 }}
              onChange={(e) => handleChangeFilter('keywordType', e)}
              options={[
                { label: '업체명', value: 'shop_information_name' },
                { label: '코드번호', value: 'shop_information_id' },
                { label: '주소', value: 'shop_information_address' },
                { label: '사업자번호', value: 'business_num' },
              ]}
            />
          ),
        },
        {
          input: (
            <Input
              style={{ ...styles.form.input.large }}
              value={searchFilter.keyword}
              onChange={(e) => handleChangeFilter('keyword', e.target.value)}
              placeholder="내용을 입력해주세요"
            />
          ),
        },
      ],
    },
    {
      label: '업체 가입일',
      form: [
        {
          input: (
            <Radio.Group
              value={searchFilter.dateType}
              onChange={(e) => handleChangeFilter('dateType', e.target.value)}
              buttonStyle="solid"
              style={{ marginRight: 12 }}
            >
              <Radio.Button value="all">전체</Radio.Button>
              <Radio.Button value="week">1주일(7일)</Radio.Button>
              <Radio.Button value="month">1개월(30일)</Radio.Button>
              <Radio.Button value="3month">3개월(90일)</Radio.Button>
            </Radio.Group>
          ),
        },
        {
          input: (
            <>
              <DatePicker
                format={'YYYY-MM-DD'}
                placeholder="시작일"
                onChange={(d, t) => handleChangeFilter('startDate', t)}
              />
              <p style={{ margin: '0px 4px' }}>~</p>
              <DatePicker
                format={'YYYY-MM-DD'}
                placeholder="종료일"
                onChange={(d, t) => handleChangeFilter('endDate', t)}
              />
            </>
          ),
        },
      ],
    },
    {
      label: '입점상태',
      form: [
        {
          input: (
            <Radio.Group
              // size="large"
              // defaultValue="today"
              value={searchFilter.applicationStatus}
              onChange={(e) =>
                handleChangeFilter('applicationStatus', e.target.value)
              }
              buttonStyle="solid"
              style={{ marginRight: 12 }}
            >
              <Radio.Button value={'all'}>전체</Radio.Button>
              {Object.keys(APPLICATION_STATUS).map((item) => (
                <Radio.Button value={item}>
                  {APPLICATION_STATUS[item].title}
                </Radio.Button>
              ))}
            </Radio.Group>
          ),
        },
      ],
    },
  ];

  const col = [
    {
      title: '업체명',
      dataIndex: 'shop_information_name',
      width: 350,
    },
    {
      title: '업체코드',
      dataIndex: 'shop_information_id',
      width: 100,
    },
    {
      title: '등록일',
      dataIndex: 'created_at',
      width: 200,
      render: (v) => moment(v * 1000).format('YYYY-MM-DD(HH:mm:ss)'),
    },

    {
      title: '사업자등록번호',
      dataIndex: 'mall_information',
      width: 200,
      render: (v, _record) => `${v?.business_num}`,
    },
    {
      title: '주소',
      dataIndex: 'shop_information_address',
    },
    {
      title: '입점신청',
      dataIndex: 'application_status',
      width: 100,
      render: (v) => (
        <Tag color={APPLICATION_STATUS[v].color}>
          {APPLICATION_STATUS[v].title}
        </Tag>
      ),
      filters: Object.entries(APPLICATION_STATUS).map(([key, value]) => ({
        text: value?.title,
        value: key,
      })),
      onFilter: (value, record) =>
        record.application_status.indexOf(value) === 0,
    },
    {
      title: '수정요청',
      dataIndex: 'correction_status',
      width: 100,
      render: (v) => (
        <Tag color={CORRECTION_STATUS[v].color}>
          {CORRECTION_STATUS[v].title}
        </Tag>
      ),
    },
    {
      title: '브랜드여부',
      dataIndex: 'is_brand',
      width: 100,
      render: (v) => (
        <Tag color={v === 'Y' ? 'success' : 'default'}>
          {v === 'Y' ? '브랜드' : '입점업체'}
        </Tag>
      ),
      filters: [
        { text: '브랜드', value: 'Y' },
        { text: '입점업체', value: 'N' },
      ],
      onFilter: (value, record) => record.is_brand === value,
    },
  ];
  // NOTE: 테이블 가짜 데이터
  let newData = [];

  /* ===== FUNCTION ===== */

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={loading}>
        {/* 업체생성 플로팅 버튼 */}
        <div style={{ position: 'fixed', bottom: 15, right: 30, zIndex: 1 }}>
          <Button
            shape="round"
            size="large"
            type="default"
            onClick={() => {
              const url = `http://admin.${process.env.REACT_APP_DOMAIN_URL}/#/apply?mode=admin`;
              window.navigator.clipboard.writeText(url);
              MessageAlert.info('생성 URL이 복사되었습니다');
            }}
          >
            <PaperClipOutlined /> 가입링크 복사
          </Button>{' '}
          <Button
            shape="round"
            size="large"
            type="primary"
            onClick={
              () => window.open('/apply?mode=admin', 'newWindow')
              // window.open('/apply', '_blank', 'width=1100, height=980')
            }
          >
            <EditOutlined /> 입점업체 등록
          </Button>
        </div>

        {/*  */}
        <Content maxWidth={'100%'} padding={20} backgroundColor={'none'}>
          <Row style={{ marginBottom: 20 }}>
            <Col x={24}>
              <Title size={4} style={{ marginBottom: 10 }}>
                입점 목록
              </Title>

              {/* 검색필터 */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 40 }}
              >
                <ExpandableContainer borderRadius={4} padding={'10px 14px'}>
                  {formItem &&
                    formItem?.map((_item, _index) => (
                      <div
                        key={`form_${_index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 0px',
                        }}
                      >
                        <div
                          style={{
                            width: 60,
                            fontSize: 13,
                            fontWeight: '500',
                            margin: '0 30px 0 10px',
                          }}
                        >
                          <label>{_item.label}</label>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {_item.form.map((_subitem, _subkey) => (
                            <React.Fragment key={`input_${_subkey}`}>
                              {_subitem.input}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                </ExpandableContainer>

                {/* 검색/초기화 버튼 */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: 14,
                  }}
                >
                  <Button
                    size="large"
                    type="primary"
                    style={{
                      ...styles.button,
                      background: 'var(--primary-color)',
                    }}
                    onClick={handleSearch}
                  >
                    검색
                  </Button>
                  <Button
                    size="large"
                    style={{ ...styles.button }}
                    onClick={handleReset}
                  >
                    초기화
                  </Button>
                </div>
                {/* 검색/초기화 버튼 끝 */}
              </Content>
              {/* 검색필터 끝 */}

              {/* ===== 요약 ===== */}
              <Content
                maxWidth={'100%'}
                padding={0}
                backgroundColor={'none'}
                style={{ marginBottom: 5, display: 'flex' }}
              >
                {[
                  { title: '입점 업체', value: shopList?.length, suffix: '개' },
                  {
                    title: '입점 신청 업체',
                    value: shopList?.filter(
                      (item) => item.application_status === 'APPLY'
                    )?.length,
                    suffix: '개',
                  },
                  {
                    title: '심사중인 업체',
                    value: shopList?.filter(
                      (item) =>
                        item.application_status !== 'REJECT' &&
                        item.application_status !== 'APPROVE'
                    )?.length,
                    suffix: '개',
                  },
                  {
                    title: '비활성화 업체',
                    value: shopList?.filter(
                      (item) => item.application_status === 'DISABLED'
                    )?.length,
                    suffix: '개',
                  },
                ].map((_item, _index) => (
                  <div
                    key={`${_item.label}-${_index}`}
                    style={{
                      background: '#FFFFFF',
                      borderRadius: 4,
                      width: `calc(100% / 4)`,
                      marginRight: _index === 3 ? 0 : 4,
                      padding: 20,
                    }}
                  >
                    <Statistic
                      title={_item.title}
                      value={_item.value}
                      suffix={_item.suffix}
                      direction={'row'}
                      style={{ justifyContent: 'center' }}
                    />
                  </div>
                ))}
              </Content>
              {/* ===== 요약 ===== */}

              {/* ===== 검색결과 ===== */}
              <Content maxWidth={'100%'} borderRadius={4} padding={20}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <h4
                    style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}
                  >
                    검색 결과 총 <span>({data?.length})</span>건
                  </h4>
                  <Button
                    onClick={() =>
                      downloadXlsx({
                        columns: col,
                        rows: data?.map((_item) => ({
                          ..._item,
                          mall_information:
                            _item?.mall_information?.business_num,
                        })),
                        fileName: `입점목록`,
                      })
                    }
                  >
                    엑셀로 다운로드
                  </Button>
                </div>

                <Table
                  bordered
                  dataSource={data}
                  onRow={(record) => {
                    return {
                      onClick: () => setCurrentShop(record), // 행 클릭 시 이벤트 처리
                    };
                  }}
                  columns={col}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: data?.length,
                    showSizeChanger: true,
                    pageSizeOptions: ['25', '50', '75', '100'],
                    onChange: (page, pageSize) => {
                      handlePageChange(page, pageSize);
                    },
                  }}
                />
              </Content>
              {/* ===== 검색결과 끝 ===== */}
            </Col>
          </Row>
        </Content>
      </Spin>

      {/* ===== 상세모달 ===== */}
      <ModalLayout
        type="drawer"
        placement="right"
        width={'calc(100% - 250px)'}
        // width={1400}
        maxWidth={'calc(100% - 250px)'}
        // height={'95%'}
        isOpen={currentShop !== null}
        open={currentShop !== null}
        title="입점업체 상세정보"
        closable={true}
        onCancel={() => setCurrentShop(null)}
      >
        {/* <Spin spinning={isLoading}>
          <StatisticsRawData headerData={currentRow} data={rawData} />
        </Spin> */}
        <div style={{}}>
          {currentShop && (
            <BrandDetail
              defaultShopId={currentShop.shop_information_id}
              defaultShopInformation={currentShop}
              onChangeShopInformation={onChangeShopInformation}
            />
          )}
        </div>
      </ModalLayout>
    </>
  );
};

/* ===== Styles ===== */
const styles = {
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 20,
  },
  button: {
    width: 80,
    height: 40,
    fontSize: 15,
    fontWeight: 600,
    margin: '0px 4px',
  },
  form: {
    input: {
      large: {
        width: 175,
        marginRight: 8,
      },
      medium: {
        width: 100,
        marginRight: 8,
      },
    },
    button: {
      marginRight: 8,
    },
    date: {
      width: 120,
    },
    text: {
      fontSize: 13,
      fontWeight: 400,
    },
  },
};

export default BrandListPresenter;
